'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';
import Link from 'next/link';

interface Tenant {
  id: string;
  company_name: string;
  slug: string;
  logo_url: string | null;
  description: string | null;
  tagline: string | null;
  whatsapp_main: string | null;
  whatsapp_number: string | null;
  accent_color: string | null;
  accent_color_secondary: string | null;
  theme_font: string | null;
  theme_button_style: string | null;
  theme_section_layout: { hero: string; inventory: string; contact: string } | null;
  hero_title: string | null;
  hero_subtitle: string | null;
  hero_cta_text: string | null;
  show_prices: boolean;
  show_km: boolean;
  currency: string;
  enabled_sections: string[];
  social_instagram: string | null;
  social_facebook: string | null;
  social_tiktok: string | null;
  social_youtube: string | null;
  services: any;
  city: string | null;
}

interface Location {
  id: string;
  name: string;
  address: string | null;
  city: string | null;
  state: string | null;
  country: string;
  phone: string | null;
  email: string | null;
  maps_url: string | null;
  weekday_open: string | null;
  weekday_close: string | null;
  weekend_open: string | null;
  weekend_close: string | null;
  weekend_closed: boolean;
  is_primary: boolean;
}

interface Vehicle {
  id: string;
  name: string;
  brand: string | null;
  model: string | null;
  year: number | null;
  price: number | null;
  condition: string | null;
  category: string | null;
  vehicle_status: string;
  photo_urls: string[] | null;
  is_featured: boolean;
  km: number | null;
  fuel_type: string | null;
  engine: string | null;
  horsepower: string | null;
  color: string | null;
  seats: number | null;
  description: string | null;
  assigned_to: string | null;
}

function formatPrice(price: number, currency: string) {
  try {
    const locale = currency === 'COP' ? 'es-CO' : currency === 'USD' ? 'en-US' : 'en-US';
    return new Intl.NumberFormat(locale, { style: 'currency', currency, maximumFractionDigits: currency === 'COP' ? 0 : 2 }).format(price);
  } catch {
    return `$${price.toLocaleString()}`;
  }
}

export default function DealershipHomePage() {
  const params = useParams();
  const slug = params?.slug as string;
  const supabase = createClient();

  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [locations, setLocations] = useState<Location[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [brands, setBrands] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('Todos');
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [subscriberEmail, setSubscriberEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchType, setSearchType] = useState('');
  const [searchBrand, setSearchBrand] = useState('');

  const accent = tenant?.accent_color || '#C9A84C';
  const accentSecondary = tenant?.accent_color_secondary || '#C8102E';
  const siteFont = tenant?.theme_font ? `${tenant.theme_font}, sans-serif` : 'Montserrat, sans-serif';
  const btnRadius = (() => {
    switch (tenant?.theme_button_style) {
      case 'pill': return '999px';
      case 'square': return '2px';
      case 'soft': return '14px';
      default: return '8px';
    }
  })();
  const heroLayout = tenant?.theme_section_layout?.hero || 'centered';
  const inventoryLayout = tenant?.theme_section_layout?.inventory || 'grid';
  const contactLayout = tenant?.theme_section_layout?.contact || 'split';

  const loadData = useCallback(async () => {
    if (!slug) return;
    const { data: tenantData } = await supabase
      .from('tenants')
      .select('*')
      .eq('slug', slug)
      .eq('plan', 'elite')
      .eq('website_published', true)
      .single();

    if (!tenantData) { setLoading(false); return; }
    setTenant(tenantData);

    const results = await Promise.all([
      supabase.from('locations').select('*').eq('tenant_id', tenantData.id).order('is_primary', { ascending: false }),
      supabase.from('vehicles').select('*').eq('tenant_id', tenantData.id).eq('vehicle_status', 'active').order('is_featured', { ascending: false }).order('created_at', { ascending: false }),
    ]);

    setLocations(results[0].data || []);
    setVehicles(results[1].data || []);
    const uniqueBrands = [...new Set((results[1].data || []).map((v: Vehicle) => v.brand).filter(Boolean))] as string[];
    setBrands(uniqueBrands);
    setLoading(false);
  }, [slug]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (!tenant?.id) return;
    const channel = supabase
      .channel(`vehicles-${tenant.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'vehicles', filter: `tenant_id=eq.${tenant.id}` }, () => loadData())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [tenant?.id, loadData]);

  const filteredVehicles = activeCategory === 'Todos' ? vehicles
    : activeCategory === 'Nuevos' ? vehicles.filter(v => v.condition === 'Nuevo')
    : activeCategory === 'Usados' ? vehicles.filter(v => v.condition === 'Usado')
    : activeCategory === 'Acuáticos' ? vehicles.filter(v => v.category === 'seadoo')
    : activeCategory === 'Off-Road' ? vehicles.filter(v => v.category === 'can-am')
    : vehicles;

  const featuredVehicle = vehicles.find(v => v.is_featured) || vehicles[0];
  const primaryLocation = locations.find(l => l.is_primary) || locations[0];
  const whatsapp = tenant?.whatsapp_main || tenant?.whatsapp_number || '';

  const handleSubscribe = async () => {
    if (!subscriberEmail || !tenant?.id) return;
    await supabase.from('subscribers').insert({ tenant_id: tenant.id, email: subscriberEmail });
    setSubscribed(true);
  };

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (searchType) params.set('condition', searchType);
    if (searchBrand) params.set('brand', searchBrand);
    window.location.href = `/${slug}/inventario${params.toString() ? '?' + params.toString() : ''}`;
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#0A0A0A', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Montserrat, sans-serif', color: '#888888' }}>
        Cargando...
      </div>
    );
  }

  if (!tenant) {
    return (
      <div style={{ minHeight: '100vh', background: '#0A0A0A', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Montserrat, sans-serif', color: '#888888', flexDirection: 'column', gap: '16px' }}>
        <div style={{ fontSize: '48px' }}>🚗</div>
        <div style={{ fontSize: '18px', fontWeight: 700, color: '#FFFFFF' }}>Sitio no encontrado</div>
        <div>Este concesionario no está disponible o aún no ha publicado su sitio.</div>
      </div>
    );
  }

  const categories = ['Todos',
    ...(vehicles.some(v => v.condition === 'Nuevo') ? ['Nuevos'] : []),
    ...(vehicles.some(v => v.condition === 'Usado') ? ['Usados'] : []),
    ...(vehicles.some(v => v.category === 'seadoo') ? ['Acuáticos'] : []),
    ...(vehicles.some(v => v.category === 'can-am') ? ['Off-Road'] : []),
  ];

  const carouselVehicles = filteredVehicles.slice(0, 12);
  const visibleCards = 4;
  const maxIndex = Math.max(0, carouselVehicles.length - visibleCards);

  return (
    <div style={{ minHeight: '100vh', background: '#0A0A0A', fontFamily: siteFont, color: '#FFFFFF' }}>

      {/* ── NAV ── */}
      <nav style={{ position: 'sticky', top: 0, zIndex: 100, background: 'rgba(10,10,10,0.97)', backdropFilter: 'blur(16px)', borderBottom: '1px solid rgba(255,255,255,0.06)', height: '68px', display: 'flex', alignItems: 'center', padding: '0 40px', justifyContent: 'space-between' }}>
        <Link href={`/${slug}`} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '10px' }}>
          {tenant.logo_url ? (
            <img src={tenant.logo_url} alt={`${tenant.company_name} logo`} style={{ height: '36px', width: 'auto', objectFit: 'contain' }} />
          ) : (
            <span style={{ fontWeight: 900, fontSize: '20px', color: '#FFFFFF', letterSpacing: '0.05em', textTransform: 'uppercase' }}>{tenant.company_name}</span>
          )}
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: '32px' }}>
          <Link href={`/${slug}`} style={{ fontSize: '13px', fontWeight: 500, color: '#FFFFFF', textDecoration: 'none' }}>Inicio</Link>
          <a href="#inventario" style={{ fontSize: '13px', fontWeight: 500, color: '#888888', textDecoration: 'none' }}>Inventario</a>
          <a href="#contacto" style={{ fontSize: '13px', fontWeight: 500, color: '#888888', textDecoration: 'none' }}>Contacto</a>
          <Link href={`/${slug}/vende`} style={{ fontSize: '13px', fontWeight: 500, color: '#888888', textDecoration: 'none' }}>Vende tu auto</Link>
          <Link href={`/${slug}/inventario`} style={{ background: '#FFFFFF', color: '#0A0A0A', fontSize: '12px', fontWeight: 700, padding: '9px 20px', borderRadius: btnRadius, textDecoration: 'none', letterSpacing: '0.04em' }}>
            Ver inventario
          </Link>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section style={{ minHeight: '92vh', background: 'linear-gradient(160deg, #111111 0%, #0A0A0A 60%, #0D0D0D 100%)', display: 'flex', flexDirection: 'column', alignItems: heroLayout === 'left' ? 'flex-start' : 'center', justifyContent: 'center', padding: heroLayout === 'fullscreen' ? '0' : '80px 40px 60px', position: 'relative', overflow: 'hidden' }}>
        {/* Background vehicle image */}
        {featuredVehicle?.photo_urls?.[0] && (
          <div style={{ position: 'absolute', inset: 0, background: `url(${featuredVehicle.photo_urls[0]}) center 60%/cover no-repeat`, opacity: heroLayout === 'fullscreen' ? 0.35 : 0.18 }} />
        )}
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at center bottom, rgba(10,10,10,0) 0%, rgba(10,10,10,0.9) 70%)' }} />

        {/* Hero content */}
        <div style={{ position: 'relative', zIndex: 2, textAlign: heroLayout === 'left' ? 'left' : 'center', maxWidth: '900px', width: '100%', padding: heroLayout === 'fullscreen' ? '0 40px' : undefined, marginLeft: heroLayout === 'left' ? '40px' : undefined }}>
          <h1 style={{ fontWeight: 900, fontSize: 'clamp(40px, 7vw, 80px)', color: '#FFFFFF', letterSpacing: '-0.02em', lineHeight: 1.05, marginBottom: '40px', textTransform: 'uppercase' }}>
            {tenant.hero_title || 'Encuentra tu auto ideal'}
          </h1>

          {/* Search bar */}
          <div style={{ background: 'rgba(20,20,20,0.9)', backdropFilter: 'blur(12px)', borderRadius: '12px', padding: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '32px', border: '1px solid rgba(255,255,255,0.08)', maxWidth: '760px', margin: heroLayout === 'left' ? '0 0 32px' : '0 auto 32px' }}>
            <select
              value={searchType}
              onChange={e => setSearchType(e.target.value)}
              style={{ flex: 1, minWidth: '140px', background: '#1A1A1A', border: '1px solid #2A2A2A', borderRadius: '8px', padding: '12px 14px', fontSize: '13px', color: searchType ? '#FFFFFF' : '#888888', fontFamily: siteFont, cursor: 'pointer', outline: 'none' }}
            >
              <option value="">Tipo de auto</option>
              <option value="Nuevo">Nuevos</option>
              <option value="Usado">Usados</option>
            </select>
            <select
              value={searchBrand}
              onChange={e => setSearchBrand(e.target.value)}
              style={{ flex: 1, minWidth: '140px', background: '#1A1A1A', border: '1px solid #2A2A2A', borderRadius: '8px', padding: '12px 14px', fontSize: '13px', color: searchBrand ? '#FFFFFF' : '#888888', fontFamily: siteFont, cursor: 'pointer', outline: 'none' }}
            >
              <option value="">Cualquier marca</option>
              {brands.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
            <select
              style={{ flex: 1, minWidth: '140px', background: '#1A1A1A', border: '1px solid #2A2A2A', borderRadius: '8px', padding: '12px 14px', fontSize: '13px', color: '#888888', fontFamily: siteFont, cursor: 'pointer', outline: 'none' }}
            >
              <option>Cualquier modelo</option>
            </select>
            <button
              onClick={handleSearch}
              style={{ background: accent, color: '#0A0A0A', border: 'none', borderRadius: btnRadius, padding: '12px 28px', fontSize: '14px', fontWeight: 800, cursor: 'pointer', fontFamily: siteFont, letterSpacing: '0.04em', whiteSpace: 'nowrap' }}
            >
              Buscar
            </button>
          </div>

          {tenant.hero_subtitle && (
            <p style={{ fontSize: '15px', color: '#AAAAAA', lineHeight: 1.7, maxWidth: '500px', margin: heroLayout === 'left' ? '0 0 28px' : '0 auto 28px' }}>{tenant.hero_subtitle}</p>
          )}
        </div>

        {/* Location card bottom-right */}
        {primaryLocation && (
          <div style={{ position: 'absolute', bottom: '32px', right: '40px', background: 'rgba(255,255,255,0.97)', borderRadius: '12px', padding: '14px 18px', display: 'flex', alignItems: 'center', gap: '10px', color: '#0A0A0A', boxShadow: '0 8px 32px rgba(0,0,0,0.4)', zIndex: 3 }}>
            <span style={{ fontSize: '20px' }}>📍</span>
            <div>
              <div style={{ fontSize: '11px', fontWeight: 700, color: accent, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '2px' }}>
                <a href="#contacto" style={{ color: accent, textDecoration: 'underline', fontWeight: 700 }}>Ver en el mapa</a>
              </div>
              <div style={{ fontSize: '12px', fontWeight: 600, color: '#0A0A0A' }}>{primaryLocation.address}</div>
              <div style={{ fontSize: '11px', color: '#555555' }}>{primaryLocation.city}{primaryLocation.state ? `, ${primaryLocation.state}` : ''}</div>
            </div>
          </div>
        )}

        {/* Description bottom-left */}
        {tenant.description && (
          <div style={{ position: 'absolute', bottom: '32px', left: '40px', maxWidth: '280px', zIndex: 3 }}>
            <p style={{ fontSize: '12px', color: '#888888', lineHeight: 1.7 }}>{tenant.description}</p>
          </div>
        )}
      </section>

      {/* ── BRAND MARQUEE ── */}
      {brands.length > 0 && (
        <section style={{ background: '#111111', padding: '32px 0', overflow: 'hidden', borderTop: '1px solid rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
          <div style={{ display: 'flex', gap: '64px', animation: 'marquee 24s linear infinite', whiteSpace: 'nowrap' }}>
            {[...brands, ...brands, ...brands].map((brand, i) => (
              <span key={i} style={{ fontSize: '14px', fontWeight: 800, color: '#444444', letterSpacing: '0.2em', textTransform: 'uppercase', flexShrink: 0 }}>
                {brand}
              </span>
            ))}
          </div>
          <style>{`@keyframes marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-33.33%); } }`}</style>
        </section>
      )}

      {/* ── WHAT WE OFFER ── */}
      <section style={{ padding: '80px 40px', background: '#0A0A0A' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '80px', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontWeight: 900, fontSize: 'clamp(28px, 4vw, 48px)', color: '#FFFFFF', marginBottom: '20px', lineHeight: 1.1, textTransform: 'uppercase' }}>
              Lo que ofrecemos
            </h2>
            <p style={{ fontSize: '14px', color: '#888888', lineHeight: 1.8, marginBottom: '36px', maxWidth: '400px' }}>
              {tenant.description || 'Obtén el mejor valor por tu vehículo con nuestro proceso transparente y directo.'}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
              {[
                ['01', 'Inventario verificado y certificado'],
                ['02', 'Atención personalizada sin presión'],
                ...(tenant.services?.financiamiento ? [['03', 'Financiamiento disponible']] : [['03', 'Prueba de manejo sin compromiso']]),
                ['04', 'Proceso transparente de compra'],
              ].map(([num, text], i, arr) => (
                <div key={num} style={{ display: 'flex', alignItems: 'center', gap: '20px', padding: '16px 0', borderBottom: i < arr.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
                  <span style={{ fontWeight: 900, fontSize: '13px', color: '#444444', minWidth: '28px', letterSpacing: '0.05em' }}>{num}</span>
                  <span style={{ fontSize: '14px', color: '#CCCCCC', fontWeight: 500 }}>{text}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div style={{ background: '#111111', borderRadius: '16px', overflow: 'hidden', position: 'relative', minHeight: '220px', gridColumn: '1 / -1' }}>
              {featuredVehicle?.photo_urls?.[0] ? (
                <img src={featuredVehicle.photo_urls[0]} alt={featuredVehicle.name || 'Featured vehicle'} style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', inset: 0 }} />
              ) : null}
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(10,10,10,0.8) 0%, transparent 60%)' }} />
              <div style={{ position: 'absolute', bottom: '20px', left: '20px' }}>
                <div style={{ fontWeight: 700, fontSize: '16px', color: '#FFFFFF' }}>Supercars</div>
                <div style={{ fontSize: '12px', color: '#AAAAAA', marginTop: '2px' }}>Explora nuestra selección premium</div>
              </div>
            </div>
            <div style={{ background: accent, borderRadius: '16px', padding: '28px' }}>
              <div style={{ fontWeight: 900, fontSize: '52px', color: '#0A0A0A', lineHeight: 1 }}>+{vehicles.length}</div>
              <div style={{ fontWeight: 600, fontSize: '13px', color: 'rgba(10,10,10,0.7)', marginTop: '6px' }}>Vehículos de diferentes marcas</div>
            </div>
            <div style={{ background: '#111111', borderRadius: '16px', padding: '28px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <div style={{ fontWeight: 900, fontSize: '36px', color: '#FFFFFF', lineHeight: 1 }}>{brands.length}+</div>
              <div style={{ fontWeight: 600, fontSize: '13px', color: '#888888', marginTop: '6px' }}>Marcas disponibles</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FEATURED INVENTORY ── */}
      <section id="inventario" style={{ padding: '80px 40px', background: '#0A0A0A' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ marginBottom: '32px' }}>
            <h2 style={{ fontWeight: 900, fontSize: 'clamp(28px, 4vw, 48px)', color: '#FFFFFF', margin: '0 0 8px', textTransform: 'uppercase' }}>
              Los más buscados
            </h2>
          </div>

          {/* Category pills */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '32px', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {categories.map(cat => (
                <button key={cat} onClick={() => { setActiveCategory(cat); setCarouselIndex(0); }} style={{ background: activeCategory === cat ? '#FFFFFF' : 'transparent', color: activeCategory === cat ? '#0A0A0A' : '#888888', border: `1px solid ${activeCategory === cat ? '#FFFFFF' : '#2A2A2A'}`, borderRadius: btnRadius, padding: '8px 18px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', fontFamily: siteFont, transition: 'all 0.2s ease' }}>
                  {cat}
                </button>
              ))}
            </div>
            <Link href={`/${slug}/inventario`} style={{ background: accent, color: '#0A0A0A', fontWeight: 700, fontSize: '13px', padding: '9px 20px', borderRadius: btnRadius, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
              Ver todos →
            </Link>
          </div>

          {/* Inventory display */}
          {inventoryLayout === 'list' ? (
            /* List layout */
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {carouselVehicles.map(vehicle => (
                <Link key={vehicle.id} href={`/${slug}/vehiculo/${vehicle.id}`} style={{ textDecoration: 'none', display: 'flex', background: '#111111', border: '0.5px solid rgba(255,255,255,0.07)', borderRadius: '12px', overflow: 'hidden', transition: 'all 0.2s ease', cursor: 'pointer', alignItems: 'center' }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.15)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.07)'; }}
                >
                  <div style={{ width: '160px', height: '110px', flexShrink: 0, background: '#1A1A1A', position: 'relative', overflow: 'hidden' }}>
                    {vehicle.photo_urls?.[0] ? (
                      <img src={vehicle.photo_urls[0]} alt={vehicle.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '32px', color: '#2A2A2A' }}>🚗</div>
                    )}
                  </div>
                  <div style={{ padding: '16px 20px', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
                    <div>
                      <div style={{ fontSize: '15px', fontWeight: 800, color: '#FFFFFF', marginBottom: '4px' }}>{vehicle.brand} {vehicle.name}</div>
                      <div style={{ fontSize: '12px', color: '#888888' }}>{vehicle.model}{vehicle.year ? `, ${vehicle.year}` : ''}{vehicle.km !== null && tenant.show_km ? ` · ${vehicle.km.toLocaleString()} km` : ''}</div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      {vehicle.price && tenant.show_prices && (
                        <div style={{ fontSize: '17px', fontWeight: 900, color: '#FFFFFF' }}>{formatPrice(vehicle.price, tenant.currency)}</div>
                      )}
                      <span style={{ fontSize: '12px', fontWeight: 700, color: accent }}>Ver detalles →</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : inventoryLayout === 'masonry' ? (
            /* Masonry layout */
            <div style={{ columns: '3', columnGap: '16px' }}>
              {carouselVehicles.map((vehicle, idx) => (
                <Link key={vehicle.id} href={`/${slug}/vehiculo/${vehicle.id}`} style={{ textDecoration: 'none', display: 'block', background: '#111111', border: '0.5px solid rgba(255,255,255,0.07)', borderRadius: '12px', overflow: 'hidden', marginBottom: '16px', breakInside: 'avoid', cursor: 'pointer' }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.15)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.07)'; }}
                >
                  <div style={{ height: idx % 3 === 0 ? '240px' : '180px', background: '#1A1A1A', position: 'relative', overflow: 'hidden' }}>
                    {vehicle.photo_urls?.[0] ? (
                      <img src={vehicle.photo_urls[0]} alt={vehicle.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '40px', color: '#2A2A2A' }}>🚗</div>
                    )}
                  </div>
                  <div style={{ padding: '12px' }}>
                    <div style={{ fontSize: '14px', fontWeight: 800, color: '#FFFFFF', marginBottom: '2px' }}>{vehicle.brand} {vehicle.name}</div>
                    {vehicle.price && tenant.show_prices && (
                      <div style={{ fontSize: '14px', fontWeight: 900, color: accent }}>{formatPrice(vehicle.price, tenant.currency)}</div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            /* Default grid layout */
            <div style={{ position: 'relative' }}>
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(visibleCards, Math.max(1, carouselVehicles.length))}, 1fr)`, gap: '16px' }}>
                {carouselVehicles.slice(carouselIndex, carouselIndex + visibleCards).map(vehicle => (
                  <Link key={vehicle.id} href={`/${slug}/vehiculo/${vehicle.id}`} style={{ textDecoration: 'none', display: 'block', background: '#111111', border: '0.5px solid rgba(255,255,255,0.07)', borderRadius: '12px', overflow: 'hidden', transition: 'all 0.2s ease', cursor: 'pointer' }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-4px)'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.15)'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.07)'; }}
                  >
                    <div style={{ height: '200px', background: '#1A1A1A', position: 'relative', overflow: 'hidden' }}>
                      {vehicle.photo_urls?.[0] ? (
                        <img src={vehicle.photo_urls[0]} alt={vehicle.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '48px', color: '#2A2A2A' }}>🚗</div>
                      )}
                      {vehicle.condition && (
                        <div style={{ position: 'absolute', top: '10px', left: '10px', background: vehicle.condition === 'Nuevo' ? accent : 'rgba(0,0,0,0.7)', color: vehicle.condition === 'Nuevo' ? '#0A0A0A' : '#FFFFFF', fontSize: '10px', fontWeight: 700, padding: '3px 8px', borderRadius: btnRadius, letterSpacing: '0.05em', backdropFilter: 'blur(4px)' }}>
                          {vehicle.condition.toUpperCase()}
                        </div>
                      )}
                      <div style={{ position: 'absolute', top: '10px', right: '10px', width: '28px', height: '28px', background: 'rgba(0,0,0,0.5)', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}>
                        <span style={{ fontSize: '12px' }}>🔖</span>
                      </div>
                    </div>
                    <div style={{ padding: '14px' }}>
                      <div style={{ fontSize: '16px', fontWeight: 800, color: '#FFFFFF', marginBottom: '3px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{vehicle.brand} {vehicle.name}</div>
                      {vehicle.model && <div style={{ fontSize: '11px', color: '#888888', marginBottom: '10px' }}>{vehicle.model}{vehicle.year ? `, ${vehicle.year}` : ''}</div>}
                      <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
                        {vehicle.km !== null && vehicle.km !== undefined && tenant.show_km && (
                          <span style={{ fontSize: '11px', color: '#888888', display: 'flex', alignItems: 'center', gap: '3px' }}>🛣 {vehicle.km.toLocaleString()} km</span>
                        )}
                        {vehicle.fuel_type && <span style={{ fontSize: '11px', color: '#888888' }}>⛽ {vehicle.fuel_type}</span>}
                        {vehicle.engine && <span style={{ fontSize: '11px', color: '#888888' }}>⚙️ {vehicle.engine}</span>}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          {vehicle.price && tenant.show_prices && (
                            <div style={{ fontSize: '17px', fontWeight: 900, color: '#FFFFFF' }}>
                              {formatPrice(vehicle.price, tenant.currency)}
                            </div>
                          )}
                        </div>
                        <span style={{ fontSize: '12px', fontWeight: 700, color: accent }}>Ver detalles →</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {carouselVehicles.length > visibleCards && inventoryLayout === 'grid' && (
                <div style={{ display: 'flex', gap: '10px', marginTop: '28px' }}>
                  <button onClick={() => setCarouselIndex(Math.max(0, carouselIndex - 1))} disabled={carouselIndex === 0} style={{ width: '40px', height: '40px', borderRadius: btnRadius, border: '1px solid #2A2A2A', background: 'transparent', color: '#FFFFFF', cursor: carouselIndex === 0 ? 'not-allowed' : 'pointer', fontSize: '16px', opacity: carouselIndex === 0 ? 0.3 : 1 }}>←</button>
                  <button onClick={() => setCarouselIndex(Math.min(maxIndex, carouselIndex + 1))} disabled={carouselIndex >= maxIndex} style={{ width: '40px', height: '40px', borderRadius: btnRadius, border: 'none', background: accent, color: '#0A0A0A', cursor: carouselIndex >= maxIndex ? 'not-allowed' : 'pointer', fontSize: '16px', fontWeight: 700, opacity: carouselIndex >= maxIndex ? 0.3 : 1 }}>→</button>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* ── SELL YOUR VEHICLE ── */}
      <section style={{ padding: '0 40px 80px', background: '#0A0A0A' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', borderRadius: '20px', overflow: 'hidden', background: 'linear-gradient(135deg, #1A1A2E 0%, #16213E 50%, #1A1A2E 100%)', display: 'grid', gridTemplateColumns: '1fr 1fr', minHeight: '380px' }}>
          <div style={{ padding: '64px 56px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <h2 style={{ fontWeight: 900, fontSize: 'clamp(24px, 3.5vw, 40px)', color: '#FFFFFF', textTransform: 'uppercase', lineHeight: 1.15, marginBottom: '16px' }}>
              Vende tu auto a nuestro concesionario
            </h2>
            <p style={{ fontSize: '14px', color: '#888888', lineHeight: 1.8, marginBottom: '32px', maxWidth: '340px' }}>
              Estamos comprometidos a ofrecer a nuestros clientes un servicio excepcional.
            </p>
            <Link href={`/${slug}/vende`} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: accent, color: '#0A0A0A', fontWeight: 800, fontSize: '14px', padding: '14px 28px', borderRadius: btnRadius, textDecoration: 'none', letterSpacing: '0.04em', alignSelf: 'flex-start' }}>
              Vender el auto →
            </Link>
          </div>
          <div style={{ position: 'relative', overflow: 'hidden' }}>
            {vehicles[1]?.photo_urls?.[0] ? (
              <img src={vehicles[1].photo_urls[0]} alt="Vende tu vehículo" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }} />
            ) : vehicles[0]?.photo_urls?.[0] ? (
              <img src={vehicles[0].photo_urls[0]} alt="Vende tu vehículo" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }} />
            ) : (
              <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '80px', color: '#2A2A2A' }}>🚗</div>
            )}
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(26,26,46,0.4) 0%, transparent 60%)' }} />
          </div>
        </div>
      </section>

      {/* ── CONTACT / MAP ── */}
      {primaryLocation && (
        <section id="contacto" style={{ padding: '80px 40px', background: '#0A0A0A' }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: contactLayout === 'stacked' ? '1fr' : contactLayout === 'minimal' ? '1fr' : '1fr 1fr', gap: contactLayout === 'stacked' ? '40px' : '64px', alignItems: 'start' }}>
            <div>
              <h2 style={{ fontWeight: 900, fontSize: 'clamp(24px, 4vw, 44px)', color: '#FFFFFF', marginBottom: '16px', lineHeight: 1.15, textTransform: 'uppercase' }}>
                Comienza tu experiencia con nosotros hoy
              </h2>
              <p style={{ fontSize: '14px', color: '#888888', lineHeight: 1.8, marginBottom: '28px', maxWidth: '440px' }}>
                Visita nuestro showroom para experimentar nuestra amplia selección de vehículos y servicio al cliente de primer nivel.
              </p>
              <Link href={`/${slug}/contacto`} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: accent, color: '#0A0A0A', fontWeight: 800, fontSize: '14px', padding: '13px 26px', borderRadius: btnRadius, textDecoration: 'none', marginBottom: '36px' }}>
                Contáctanos →
              </Link>

              {contactLayout !== 'minimal' && (
                <div style={{ background: '#111111', borderRadius: '12px', padding: '24px 28px', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <div style={{ fontWeight: 700, fontSize: '14px', color: '#FFFFFF', marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>Horario de atención</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                    <div>
                      <div style={{ fontSize: '10px', fontWeight: 700, color: '#555555', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '6px' }}>ENTRE SEMANA</div>
                      <div style={{ fontSize: '15px', fontWeight: 700, color: '#FFFFFF' }}>
                        {primaryLocation.weekday_open || '8:30 AM'} — {primaryLocation.weekday_close || '9:30 PM'}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '10px', fontWeight: 700, color: '#555555', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '6px' }}>FINES DE SEMANA</div>
                      <div style={{ fontSize: '15px', fontWeight: 700, color: primaryLocation.weekend_closed ? '#555555' : '#FFFFFF' }}>
                        {primaryLocation.weekend_closed ? 'Cerrado' : `${primaryLocation.weekend_open || '8:30 AM'} — ${primaryLocation.weekend_close || '9:30 PM'}`}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {contactLayout !== 'stacked' && (
              <div>
                <div style={{ background: '#111111', borderRadius: '16px', overflow: 'hidden', marginBottom: '16px', border: '1px solid rgba(255,255,255,0.06)' }}>
                  {primaryLocation.maps_url ? (
                    <iframe src={primaryLocation.maps_url} width="100%" height="320" style={{ border: 'none', display: 'block', filter: 'grayscale(0.8) invert(0.9) hue-rotate(180deg)' }} loading="lazy" title="Mapa" />
                  ) : (
                    <div style={{ height: '320px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '48px', color: '#2A2A2A', background: '#111111' }}>🗺️</div>
                  )}
                  <div style={{ padding: '14px 18px', background: 'rgba(255,255,255,0.95)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '16px' }}>📍</span>
                    <span style={{ fontSize: '12px', fontWeight: 600, color: '#0A0A0A' }}>{primaryLocation.address}, {primaryLocation.city}</span>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  {primaryLocation.email && (
                    <a href={`mailto:${primaryLocation.email}`} style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#111111', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '14px 16px', textDecoration: 'none', color: '#CCCCCC', fontSize: '12px', fontWeight: 500 }}>
                      <span style={{ fontSize: '18px' }}>✉️</span>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{primaryLocation.email}</span>
                    </a>
                  )}
                  {primaryLocation.phone && (
                    <a href={`tel:${primaryLocation.phone}`} style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#111111', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '14px 16px', textDecoration: 'none', color: '#CCCCCC', fontSize: '12px', fontWeight: 500 }}>
                      <span style={{ fontSize: '18px' }}>📞</span>
                      <span>{primaryLocation.phone}</span>
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── FOOTER ── */}
      <footer style={{ background: '#FFFFFF', borderRadius: '20px 20px 0 0', padding: '56px 40px 28px', margin: '0 16px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 1.5fr', gap: '40px', marginBottom: '48px' }}>
            <div>
              {tenant.logo_url ? (
                <img src={tenant.logo_url} alt={`${tenant.company_name} logo`} style={{ height: '40px', width: 'auto', objectFit: 'contain', marginBottom: '12px' }} />
              ) : (
                <div style={{ fontWeight: 900, fontSize: '22px', color: '#0A0A0A', marginBottom: '12px', letterSpacing: '0.04em', textTransform: 'uppercase', fontFamily: siteFont }}>{tenant.company_name}</div>
              )}
              {tenant.tagline && <div style={{ fontSize: '13px', color: '#888888', marginBottom: '12px', lineHeight: 1.6 }}>{tenant.tagline}</div>}
              {primaryLocation?.phone && <div style={{ fontSize: '13px', color: '#555555', marginBottom: '4px' }}>📞 {primaryLocation.phone}</div>}
              {primaryLocation?.address && <div style={{ fontSize: '13px', color: '#555555' }}>📍 {primaryLocation.address}</div>}
              {primaryLocation?.city && <div style={{ fontSize: '13px', color: '#555555' }}>{primaryLocation.city}</div>}
            </div>

            <div>
              <div style={{ fontWeight: 700, fontSize: '13px', color: '#0A0A0A', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '20px' }}>Menú</div>
              {[['Inicio', `/${slug}`], ['Sobre nosotros', `/${slug}/contacto`], ['Inventario', `/${slug}/inventario`], ['Servicios', `/${slug}/contacto`]].map(([label, href]) => (
                <Link key={href} href={href} style={{ display: 'block', fontSize: '13px', color: '#555555', textDecoration: 'none', marginBottom: '10px' }}>{label}</Link>
              ))}
            </div>

            <div>
              <div style={{ fontWeight: 700, fontSize: '13px', color: '#0A0A0A', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '20px' }}>Accesos rápidos</div>
              {whatsapp && <a href={`https://wa.me/${whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" style={{ display: 'block', fontSize: '13px', color: '#555555', textDecoration: 'none', marginBottom: '10px' }}>WhatsApp</a>}
              <Link href={`/${slug}/contacto`} style={{ display: 'block', fontSize: '13px', color: '#555555', textDecoration: 'none', marginBottom: '10px' }}>Contacto</Link>
              <Link href={`/${slug}/vende`} style={{ display: 'block', fontSize: '13px', color: '#555555', textDecoration: 'none', marginBottom: '10px' }}>Vende tu auto</Link>
              <Link href={`/${slug}/inventario`} style={{ display: 'block', fontSize: '13px', color: '#555555', textDecoration: 'none', marginBottom: '10px' }}>Mi cuenta</Link>
            </div>

            <div>
              <div style={{ fontWeight: 700, fontSize: '15px', color: '#0A0A0A', marginBottom: '8px' }}>Suscríbete a nuestro newsletter</div>
              <p style={{ fontSize: '13px', color: '#888888', marginBottom: '20px', lineHeight: 1.6 }}>Mantente al día con las últimas noticias, promociones y ofertas exclusivas.</p>
              {subscribed ? (
                <div style={{ fontSize: '13px', color: '#10B981', fontWeight: 600 }}>✓ ¡Suscrito con éxito!</div>
              ) : (
                <div style={{ display: 'flex', gap: '0' }}>
                  <input type="email" value={subscriberEmail} onChange={e => setSubscriberEmail(e.target.value)} placeholder="Email" style={{ flex: 1, background: '#F5F5F5', border: '1px solid #E5E5E5', borderRadius: `${btnRadius} 0 0 ${btnRadius}`, padding: '11px 14px', fontSize: '13px', color: '#0A0A0A', fontFamily: siteFont, outline: 'none' }} />
                  <button onClick={handleSubscribe} style={{ background: '#0A0A0A', color: '#FFFFFF', border: 'none', borderRadius: `0 ${btnRadius} ${btnRadius} 0`, padding: '11px 18px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', fontFamily: siteFont, whiteSpace: 'nowrap' }}>
                    Suscribirse
                  </button>
                </div>
              )}
            </div>
          </div>

          <div style={{ borderTop: '1px solid #E5E5E5', paddingTop: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
            <span style={{ fontSize: '12px', color: '#888888' }}>© {new Date().getFullYear()} {tenant.company_name}. Todos los derechos reservados.</span>
            <div style={{ display: 'flex', gap: '12px' }}>
              {tenant.social_instagram && (
                <a href={`https://instagram.com/${tenant.social_instagram.replace('@', '')}`} target="_blank" rel="noopener noreferrer" style={{ width: '36px', height: '36px', background: '#0A0A0A', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', textDecoration: 'none' }}>📸</a>
              )}
              {tenant.social_facebook && (
                <a href={tenant.social_facebook} target="_blank" rel="noopener noreferrer" style={{ width: '36px', height: '36px', background: '#0A0A0A', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', textDecoration: 'none' }}>👍</a>
              )}
              {tenant.social_tiktok && (
                <a href={`https://tiktok.com/${tenant.social_tiktok.replace('@', '')}`} target="_blank" rel="noopener noreferrer" style={{ width: '36px', height: '36px', background: '#0A0A0A', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', textDecoration: 'none' }}>🎵</a>
              )}
              {tenant.social_youtube && (
                <a href={tenant.social_youtube} target="_blank" rel="noopener noreferrer" style={{ width: '36px', height: '36px', background: '#0A0A0A', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', textDecoration: 'none' }}>▶️</a>
              )}
            </div>
          </div>
        </div>
      </footer>

      {/* ── FLOATING WHATSAPP ── */}
      {whatsapp && (
        <a
          href={`https://wa.me/${whatsapp.replace(/\D/g, '')}?text=Hola, me gustaría obtener más información sobre sus vehículos.`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ position: 'fixed', bottom: '24px', right: '24px', width: '56px', height: '56px', background: '#25D366', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px', boxShadow: '0 4px 20px rgba(37,211,102,0.4)', zIndex: 200, textDecoration: 'none' }}
        >
          💬
        </a>
      )}
    </div>
  );
}
